import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
/* Components */
import { Modal, Form, InputGroup } from "react-bootstrap";
import WrongNetworkAlert from "./WrongNetworkAlert";
import TxButton from "../CommonForBoth/TxButton";
/* Helpers */
import { strToDec, strToDecZ, strToBN } from "@/helpers/number_format_helper";
import { isTransactionValid } from "@/helpers/store_helper";
import { useModalTxError } from "@/hooks/useModalTxError";
/* Store */
import { selectEthCallMultiple, selectLastTransact, addRemoveEthSub } from "@ensuro/ethereum-store";
import { selectCurrent } from "@store/eTokens/selectors";
import { selectChainId } from "@/store/user/selectors";
/* i18n */
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { chain } from "@/config";
import "./style.scss";

const makeEthCalls = function (userAddress, wrapperAddress) {
  return [{ address: wrapperAddress, abi: "WEToken", method: "maxWithdraw", args: [userAddress] }];
};

const UnwrapModal = ({
  t,
  user,
  userAddress,
  lastTransaction,
  show,
  onHide,
  balance,
  wrapperAddress,
  etkSymbol,
  chainId,
}) => {
  const dispatch = useDispatch();
  const { errorToShow, clearError, wrapDispatch } = useModalTxError(lastTransaction);
  const [userAmount, setUserAmount] = useState("");
  const ready = balance.state === "LOADED" && chainId === chain.id;

  const handleHide = () => {
    clearError();
    onHide();
  };

  useEffect(() => {
    if (userAddress && wrapperAddress) {
      return addRemoveEthSub(dispatch, "unwrapModal", makeEthCalls(userAddress, wrapperAddress));
    }
  }, [dispatch, userAddress, wrapperAddress]);

  const invalidBalance = () => {
    return !balance.value || (strToDec(userAmount) && strToDec(userAmount).gt(balance.value));
  };

  const dispatchUnwrap = () => {
    if (!isTransactionValid(lastTransaction) || !userAddress) return;
    const amountBN = strToBN(userAmount);
    if (amountBN == null) return;
    dispatch({
      type: "ETH_TRANSACT",
      address: wrapperAddress,
      abi: "WEToken",
      method: "withdraw",
      args: [amountBN, userAddress, userAddress],
    });
  };

  return (
    <Modal show={show} centered onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {t("Unwrap")} {etkSymbol}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {chainId !== chain.id && <WrongNetworkAlert />}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("Amount")}</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="0.00"
                id="unwrap-amount"
                isInvalid={userAmount !== "" && (!strToDecZ(userAmount) || invalidBalance())}
                value={userAmount}
                onChange={(e) => setUserAmount(e.target.value || "")}
              />
              <InputGroup.Text>{etkSymbol}</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {!strToDecZ(userAmount)
                  ? t("Please provide a valid amount.")
                  : invalidBalance() && <span>{t("Your balance is not enough.")}</span>}
              </Form.Control.Feedback>
            </InputGroup>
            <p className="align-end">
              {t("Your balance")}: {balance.value != null ? balance.value * 1 : "—"}
              <strong
                onClick={() => setUserAmount(balance.value != null ? balance.value.toString() : "")}
                className="btn-text"
              >
                {t("MAX")}
              </strong>
            </p>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <em style={{ color: "red" }}>{errorToShow ?? ""}</em>
        <TxButton
          chainId={chainId}
          onClick={wrapDispatch(dispatchUnwrap)}
          disabled={!ready || !strToDecZ(userAmount) || invalidBalance() || (user && !user.canTransact)}
          text={t("Unwrap")}
        />
      </Modal.Footer>
      {user && !user.canTransact && (
        <p className="read-only">{t("Read-only mode. Connect to a wallet to perform transactions.")}</p>
      )}
    </Modal>
  );
};

UnwrapModal.propTypes = {
  t: PropTypes.any,
};

const mapStateToProps = (state) => {
  const user = state.UserReducer;
  const userAddress = user?.address;
  const etk = selectCurrent(state.ETokensReducer);
  const etkSymbol = etk?.symbol;
  const wrapperAddress = etk?.custom_data?.wrapper?.address;
  const lastTransaction = selectLastTransact(state.EthereumReducer);
  const chainId = selectChainId(state.UserReducer);

  const callsResult =
    !userAddress || !wrapperAddress
      ? null
      : selectEthCallMultiple(state.EthereumReducer, makeEthCalls(userAddress, wrapperAddress));

  const [balance] = !userAddress || !callsResult ? [{}] : callsResult;

  return {
    user,
    userAddress,
    lastTransaction,
    balance,
    wrapperAddress,
    etkSymbol,
    chainId,
  };
};

export default connect(mapStateToProps)(withTranslation()(UnwrapModal));
